/**
 * Triveny design-system tokens — CSS variable references.
 *
 * All values point at `--v2-*` custom properties defined in globals.css. The
 * v2-theme + triveny-platform.css stylesheets are the visual source of truth;
 * this module is a TypeScript-friendly accessor so JSX inline styles can stay
 * consistent across Triveny primitives (TvPill, TvCard, TvButton, …) without
 * each component re-declaring the same string literals.
 *
 * If you need a new token, add it here once and consume it everywhere.
 */
export const tvTokens = {
  // Surfaces
  ink: 'var(--v2-ink)',
  ink700: 'var(--v2-ink-700)',
  ink500: 'var(--v2-ink-500)',
  ink400: 'var(--v2-ink-400)',
  paper: 'var(--v2-paper)',
  paper2: 'var(--v2-paper-2)',
  surface: 'var(--v2-surface)',

  // Borders
  border: 'var(--v2-border)',
  borderMuted: 'var(--v2-border-muted)',

  // Semantic
  accent: 'var(--v2-accent)',
  accentSoft: 'var(--v2-accent-soft)',
  accentInk: 'var(--v2-accent-ink)',
  success: 'var(--v2-success)',
  successSoft: 'var(--v2-success-soft)',
  successInk: 'var(--v2-success-ink)',
  attention: 'var(--v2-attention)',
  attentionSoft: 'var(--v2-attention-soft)',
  attentionInk: 'var(--v2-attention-ink)',
  danger: 'var(--v2-danger)',
  dangerSoft: 'var(--v2-danger-soft)',
  dangerInk: 'var(--v2-danger-ink)',

  // Radii + shadow
  rSm: 'var(--v2-r-sm)',
  r: 'var(--v2-r)',
  rLg: 'var(--v2-r-lg)',
  shadow1: 'var(--v2-shadow-1)',
  shadow2: 'var(--v2-shadow-2)',

  // Type
  fontDisplay:
    "'Inter Tight', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
  fontSans:
    "'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
  fontMono: "'JetBrains Mono', ui-monospace, Menlo, monospace",
} as const;

/** Pill / status variant shared by TvPill and any inline pill renderers. */
export type TvVariant =
  | 'neutral'
  | 'accent'
  | 'success'
  | 'attention'
  | 'danger';
